import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, Filter, History, User, Building2, 
  FileText, Calendar, Clock, Shield, RefreshCw 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import Sidebar from '../components/Sidebar';

const ACTION_COLORS = {
  'USER_CREATE': 'bg-green-100 text-green-800',
  'USER_UPDATE': 'bg-blue-100 text-blue-800',
  'USER_DELETE': 'bg-red-100 text-red-800',
  'ORGANIZATION_CREATE': 'bg-purple-100 text-purple-800',
  'ORGANIZATION_DELETE': 'bg-red-100 text-red-800',
  'PASSWORD_RESET': 'bg-yellow-100 text-yellow-800',
  'MANUAL_JOB_TRIGGER': 'bg-orange-100 text-orange-800',
  'default': 'bg-gray-100 text-gray-800'
};

const ACTION_ICONS = {
  'USER_CREATE': User,
  'USER_UPDATE': User,
  'USER_DELETE': User,
  'ORGANIZATION_CREATE': Building2,
  'ORGANIZATION_DELETE': Building2,
  'PASSWORD_RESET': Shield,
  'MANUAL_JOB_TRIGGER': RefreshCw,
  'default': FileText
};

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionTypes, setActionTypes] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Access denied. Super admin only.');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, typesRes] = await Promise.all([
        api.get('/audit-logs', { params: { limit: 200 } }),
        api.get('/audit-logs/actions')
      ]);
      setLogs(logsRes.data.logs || []);
      setActionTypes(typesRes.data.actions || []);
      setResourceTypes(typesRes.data.resource_types || []);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (actionFilter && log.action !== actionFilter) return false;
    if (resourceFilter && log.resource_type !== resourceFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        log.user_email?.toLowerCase().includes(search) ||
        log.action?.toLowerCase().includes(search) ||
        log.resource_id?.toLowerCase().includes(search) ||
        JSON.stringify(log.details).toLowerCase().includes(search)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => ACTION_COLORS[action] || ACTION_COLORS.default;
  const getActionIcon = (action) => ACTION_ICONS[action] || ACTION_ICONS.default;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
              <History className="h-6 w-6 text-primary" />
              Audit Logs
            </h1>
            <p className="font-body text-muted-foreground">Track administrative actions</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            data-testid="refresh-logs"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, action, or details..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-logs"
                />
              </div>
              
              <Select value={actionFilter || "all"} onValueChange={(val) => setActionFilter(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={resourceFilter || "all"} onValueChange={(val) => setResourceFilter(val === "all" ? "" : val)}>
                <SelectTrigger data-testid="resource-filter">
                  <SelectValue placeholder="All Resources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  {resourceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Activity History</CardTitle>
            <CardDescription>
              Showing {filteredLogs.length} of {logs.length} audit log entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No audit logs found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLogs.map((log) => {
                  const Icon = getActionIcon(log.action);
                  return (
                    <div
                      key={log.id}
                      className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-slate-50 transition-colors"
                      data-testid={`audit-log-${log.id}`}
                    >
                      <div className={`p-2 rounded-full ${getActionColor(log.action)}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getActionColor(log.action)}>
                            {log.action}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            on {log.resource_type}
                          </span>
                          {log.resource_id && (
                            <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                              {log.resource_id.slice(0, 8)}...
                            </code>
                          )}
                        </div>
                        
                        <p className="text-sm mt-1">
                          <span className="font-medium">{log.user_email}</span>
                          <span className="text-muted-foreground"> ({log.user_role})</span>
                        </p>
                        
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground bg-slate-50 rounded p-2">
                            {Object.entries(log.details).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span>{' '}
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(log.timestamp)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
